import { ROUTES } from 'enrollment/app/routes';
import { COVERAGES_BENEFIT_CATEGORY } from 'enrollment/queries/useBenefitCartQuery/utils';
import {
  BenefitDto,
  CategoryCoverageDto,
  CategoryResponseDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import {
  BenefitPlanDto,
  CoverageDto,
  EsignatureDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { GET_BENEFIT_PLANS_QUERY_KEY } from 'enrollment/queries/useBenefitPlansQuery/utils';
import { GET_UNUM_GET_PLANS_KEY } from 'enrollment/queries/useUnumGetPlansQuery/utils';
import { DependentDto } from 'theSource/features/FamilyMembers/types';
import { queryClient, queryKeyGenerator } from 'theSource/utils';

export const checkCovLevelsMatch = (plans: BenefitPlanDto[]) => {
  if (plans.length === 0) {
    return false;
  }
  let retval = true;
  const firstPlanId = plans[0].plan_id;
  // we can assume if all plans have the same coverage levels, we can
  // safely grab the first plan's coverage levels and compare from there
  const uniqueCovLevels = plans
    .filter((plan) => plan.plan_id === firstPlanId)
    .map((plan) => plan.level_id);

  // iterate thru each plan counting each coverage level found in the array
  // if len matches, both plans share same cov levels
  let planId = firstPlanId;
  let count = 0;
  plans.forEach((plan) => {
    if (plan.plan_id !== planId && count === uniqueCovLevels.length) {
      planId = plan.plan_id;
      count = 0;
    }
    if (plan.plan_id !== planId && count !== uniqueCovLevels.length) {
      retval = false;
      return true; // break - mismatched levels
    }

    // count
    if (uniqueCovLevels.includes(plan.level_id)) {
      count++;
    } else {
      retval = false;
      return true; // break - mismatched levels
    }
  });

  return retval;
};

// filters dependents by match coverage dependents if there is a match coverage and it has req dependent ids
export const dependentsEligibleForPlan = (
  matchCoverageExists: boolean,
  dependents: DependentDto[],
  matchCoverageReqDepIds: number[]
) => {
  if (
    matchCoverageExists &&
    matchCoverageReqDepIds.length > 0 &&
    dependents?.length > 0
  ) {
    return dependents.filter((dependent) =>
      matchCoverageReqDepIds?.includes(dependent.id)
    );
  } else {
    return dependents;
  }
};

export const getShowEsignModal = (
  esignData: EsignatureDto | null,
  coverage: CategoryCoverageDto | CoverageDto | undefined,
  isAdmin: boolean,
  esigned?: boolean | true
) => {
  if (esignData === null || esignData === undefined) {
    return false;
  }

  // admin can bypass esign if allow is unchecked
  if (isAdmin && !esignData.allow_admin_esign) {
    return false;
  }

  if (esignData.esignature_required) {
    return true;
  } else if (
    esignData.requires_esign &&
    (!esigned || !coverage?.esign_data?.esigned)
  ) {
    return true;
  } else {
    return false; // could have requires esign but that is only for first election
  }
};

// this existed in the onSuccess callback for elect endpoint for old flow - keeping for records
export const handlePostElectionOLDFLOW = (
  contextLookup: string,
  opyContextId: number,
  fromReviewPage: boolean,
  backPath: string
) => {
  invalidatePostElectionQueries(contextLookup, opyContextId, 0); // putting in 0 since this is currently unused

  if (fromReviewPage) {
    return `/enrollment/${ROUTES.enrollment.review.path}`;
  } else {
    return `/enrollment/${backPath}`;
  }
};

interface PostElectionValuesType {
  navPath: string;
  benefitData?: BenefitDto;
  pendedBenefitData?: BenefitDto | undefined;
  benefitScroll?: string;
}

export const handlePostElection = (
  contextLookup: string,
  opyContextId: number,
  benefitsResponse: CategoryResponseDto,
  currentCategory: string,
  orgBenefitId: number
): PostElectionValuesType => {
  invalidatePostElectionQueries(contextLookup, opyContextId, orgBenefitId);
  const benefitsInCategoryWithCov = benefitsResponse.data.benefits
    .filter(
      (benefit) => benefit.benefit.org_benefit_category === currentCategory
    )
    .sort((a, b) => a.benefit.display_order - b.benefit.display_order);

  const benefitsInCategory = benefitsInCategoryWithCov.filter(
    (benefit) =>
      !benefit.coverage ||
      (benefit.benefit.active_enrollment_required &&
        benefitsResponse.data.active_enrollment_benefit_status[
          benefit.benefit.org_benefit_id.toString()
        ] === false)
  );
  const currentIndex = benefitsInCategory.findIndex(
    (benefit) => benefit.benefit.org_benefit_id === orgBenefitId
  );

  const benefitScroll = benefitsInCategoryWithCov.find(
    (benefit) => benefit.benefit.org_benefit_id === orgBenefitId
  )?.benefit.benefit_lookup_code;
  const nextBenefitData = benefitsInCategory[currentIndex + 1];
  const pendedBenefitData = benefitsResponse.data.pended_benefits?.filter(
    (pendedBenefits) =>
      pendedBenefits.benefit.benefit_category === currentCategory
  )[currentIndex + 1];

  if (nextBenefitData) {
    const plugin = nextBenefitData.benefit.benefit_plugin;
    let navPath = `/enrollment${ROUTES.enrollment.plans.path}`;
    if (plugin) {
      navPath = `/enrollment${ROUTES.enrollment.plugin.path}/${plugin.code}`;
    } else if (nextBenefitData.benefit.benefit_class === 'survey') {
      navPath = `/enrollment${ROUTES.enrollment.survey.path}/${nextBenefitData.benefit.org_benefit_id}`;
    }

    //used for benefit plans. this invalidates all plan queries matching this including those with and without dependents - needed if dependent plan viewed out of order
    queryClient.invalidateQueries({
      queryKey: [
        GET_BENEFIT_PLANS_QUERY_KEY +
          '-' +
          nextBenefitData.benefit.org_benefit_id,
      ],
    });

    return {
      navPath: navPath,
      benefitData: nextBenefitData,
      pendedBenefitData,
    };
  } else {
    return {
      navPath: `/enrollment/benefits`,
      benefitScroll,
    };
  }
};

export const invalidatePostElectionQueries = (
  contextLookup: string,
  opyContextId: number,
  benefitId: number
) => {
  // this is used for decisionIQ recommendations
  queryClient.invalidateQueries(
    queryKeyGenerator(`contribution-${contextLookup}`, opyContextId)
  );
  // this doesn't seem to be used anymore. Was used for cart totals
  queryClient.invalidateQueries(
    queryKeyGenerator(COVERAGES_BENEFIT_CATEGORY, opyContextId)
  );
  // used for questionnaire and review pages.
  queryClient.invalidateQueries(
    queryKeyGenerator(`benefits-${contextLookup}`, opyContextId)
  );
  // used for UNUM
  queryClient.invalidateQueries(
    `${GET_UNUM_GET_PLANS_KEY}-${contextLookup}-${benefitId}`
  );
  // this is the new cart cost query
  if (contextLookup === 'life_event') {
    queryClient.refetchQueries(
      queryKeyGenerator(`cart-${contextLookup}`, opyContextId)
    );
  }
  return;
};
